import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { isLoggedIn, selectToken } from "../login/loginSlice";
import { accountsLoading, changeAccountIdToDelete, setAccountsCheckboxes, deleteAccount, loadAccountsByBank, loadAllAccounts, removeAccount, selectAccountIdToDelete, selectAccounts, selectAccountsCheckboxes, selectConfirmingDeleteAccount, setConfirmingDeleteAccount } from "./accountsSlice";
import { loadAllBranches, loadBranches, selectBranches } from "../manageBranches/branchesSlice";

import './ManageAccounts.css';

import Sidebar from "../../components/sidebar/Sidebar";
import { toggleCreateAccountForm } from "../createAccountForm/createAccountFormSlice";
import CreateAccountForm from "../createAccountForm/CreateAccountForm";
import ConfirmAction from "../../components/confirmAction/ConfirmAction";
import { selectUser } from "../home/userSlice";
import { changeSearchMode, changeSearchTarget, changeSearchTargetOptions, selectFilters, selectSearchMode } from "../searchBar/searchBarSlice";
import SearchBar from "../searchBar/SearchBar";
import BulkTransactionsForm from "../bulkTransactionsForm/BulkTransactionsForm.js";
import { changeFormActive } from "../bulkTransactionsForm/bulkTransactionsSlice";
import Filter from "../../components/filter/Filter.js";

export default function ManageAccounts() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const token = useSelector(selectToken);

    const loggedIn = useSelector(isLoggedIn);
    const branches = useSelector(selectBranches);
    const accounts = useSelector(selectAccounts);
    const loadingAccounts = useSelector(accountsLoading);
    const accountIdToDelete = useSelector(selectAccountIdToDelete);
    const confirmingDeleteAccount = useSelector(selectConfirmingDeleteAccount);

    const userInfo = useSelector(selectUser);
    const bankId = userInfo.bankId;
    const userRole = userInfo.userRole;

    const searchMode = useSelector(selectSearchMode);
    const searchFilters = useSelector(selectFilters);
    const accountFilters = searchFilters.filter(filter => filter.searchMode === 'account');

    const filteredAccounts = accountFilters.length < 1 ? Object.values(accounts) : Object.values(accounts).filter(account => {
        let filterCheck = true;

        accountFilters.forEach(filter => {
            if (filter.active && filterCheck) {
                if (filter.searchTarget === 'Bank Id') {
                    filterCheck = account.bankId.includes(filter.searchTerm);
                } else if (filter.searchTarget === 'Account Id') {
                    filterCheck = account.accountId.includes(filter.searchTerm);
                } else if (filter.searchTarget === 'Branch') {
                    const branchName = branches.find(branch => branch.branchId === account.branchId);
                    filterCheck = branchName.branchName.includes(filter.searchTerm);
                } else if (filter.searchTarget === 'User Id') {
                    filterCheck = account.username.includes(filter.searchTerm);
                } else if (filter.searchTarget === 'Account Type') {
                    filterCheck = account.accountType.includes(filter.searchTerm);
                }
            }
        })
        return filterCheck;
    })

    const accountsCheckBoxes = useSelector(selectAccountsCheckboxes);

    useEffect(() => {
        if (!loggedIn) {
            navigate('../login');
        }
    }, [navigate, loggedIn])

    useEffect(() => {
        if (userRole === 'ADMIN') {
            dispatch(loadBranches({bankId, token}));
            dispatch(loadAccountsByBank({bankId, token}));
        } else if (userRole === 'SUPERADMIN') {
            dispatch(loadAllBranches(token));
            dispatch(loadAllAccounts(token));
        }
        
    }, [dispatch, userRole, bankId, token])

    useEffect(() => {
        if (searchMode !== 'account') {
            dispatch(changeSearchMode('account'));
            dispatch(changeSearchTargetOptions(['Bank Id', 'Account Id', 'Branch', 'User Id', 'Account Type']));
            dispatch(changeSearchTarget('Branch'));
        }
    })

    useEffect(() => {
        if (!loadingAccounts && accountsCheckBoxes.length < 1) {
            const newAccountsCheckboxes = filteredAccounts.map(account => {
                return {accountId: account.accountId, value: false}
            });
            dispatch(setAccountsCheckboxes(newAccountsCheckboxes));
        }
        
    }, [dispatch, filteredAccounts, loadingAccounts, accountsCheckBoxes]);

    const handleCreateAccountButtonClick = () => {
        dispatch(toggleCreateAccountForm());
    }

    const handleBulkTransactionsButtonClick = () => {
        dispatch(changeFormActive(true));
    }

    const handleDeleteButtonClick = (event) => {
        dispatch(setConfirmingDeleteAccount(true));
        dispatch(changeAccountIdToDelete(event.target.value));
    }

    const handleConfirmCancelClick = () => {
        dispatch(setConfirmingDeleteAccount(false));

    }

    const handleConfirmDeleteClick = () => {
        dispatch(deleteAccount({accountId: accountIdToDelete, token}));
        dispatch(setConfirmingDeleteAccount(false));
        dispatch(removeAccount(accountIdToDelete));
    }

    const handleViewAccountActivityClick = (event) => {
        navigate(`../account/${event.target.value}`)
    }

    const handleCheckboxClick = (event) => {
        const newAccountsCheckboxes = filteredAccounts.map((account, index) => {
            if (event.target.id === account.accountId) {
                return {accountId: account.accountId, value: !accountsCheckBoxes[index].value};
            } else {
                return accountsCheckBoxes[index];
            }
        })

        dispatch(setAccountsCheckboxes(newAccountsCheckboxes));
    }

    const handleTopCheckboxClick = (event) => {
        const newAccountsCheckboxes = filteredAccounts.map((account) => {
            return {accountId: account.accountId, value: event.target.checked};
        })
        dispatch(setAccountsCheckboxes(newAccountsCheckboxes));
    }

    return (
        <main>
            <Sidebar />
            <section className="manage-accounts">
                <div>
                    <h2>Manage Accounts</h2>
                </div>
                <div className="account-header">
                    <SearchBar />
                    <button onClick={handleCreateAccountButtonClick}>Create Account</button>
                </div>
                <div className='filters'>
                    {accountFilters.map(filter => <Filter filter={filter} />)}
                </div>
                <div>
                    <button onClick={handleBulkTransactionsButtonClick}>Bulk Transactions</button>
                </div>
                {loadingAccounts ? <h3>Loading Accounts...</h3> : 
                    <table>
                        <thead>
                            <tr>
                                <th>Delete Account</th>
                                {userRole === 'SUPERADMIN' ? <th>Bank Id</th> : ""}
                                <th>Account Id</th>
                                <th>Branch</th>
                                <th>User Id</th>
                                <th>Account Type</th>
                                <th>Current Balance</th>
                                <th>Account Activity</th>
                                <th><input type="checkbox" onClick={handleTopCheckboxClick}></input></th>
                            </tr>
                        </thead>
                        <tbody>
                            {!branches.length ? "" : filteredAccounts.map((account, index) => {
                                let branchName = branches.find(branch => branch.branchId === account.branchId).branchName;

                                return (
                                    <tr key={account.accountId}>
                                        <td><button value={account.accountId} onClick={handleDeleteButtonClick}>Delete</button></td>
                                        {userRole === 'SUPERADMIN' ? <td>{account.bankId}</td> : ""}
                                        <td>{account.accountId}</td>
                                        <td>{branchName}</td>
                                        <td>{account.username}</td>
                                        <td>{account.accountType}</td>
                                        <td>${account.balance.toFixed(2)}</td>
                                        <td><button value={account.accountId} onClick={handleViewAccountActivityClick}>View</button></td>
                                        <td><input type="checkbox"  onClick={handleCheckboxClick} checked={accountsCheckBoxes[index] ? accountsCheckBoxes[index].value : false} id={accountsCheckBoxes[index] ? accountsCheckBoxes[index].accountId : ""}></input></td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                }
            </section>
            <CreateAccountForm />
            <BulkTransactionsForm accountsCheckboxes={accountsCheckBoxes} />
            {confirmingDeleteAccount ? <ConfirmAction handleCancel={handleConfirmCancelClick} handleConfirm={handleConfirmDeleteClick}/> : ""}
        </main>
    )
}